import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getVersions,
  createVersion,
  updateVersion,
  deleteVersion,
  toggleVersionStatus,
  getVersionStats
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import '../../Styles/VersionManagement.css';

const appTypes = [
  { id: 'user', label: 'User App', icon: '👤', color: '#4F46E5', bgColor: '#EEF2FF' },
  { id: 'partner', label: 'Partner App', icon: '🤝', color: '#059669', bgColor: '#ECFDF5' },
  { id: 'executive', label: 'Executive App', icon: '💼', color: '#D97706', bgColor: '#FFFBEB' },
  { id: 'crm', label: 'CRM App', icon: '📊', color: '#DC2626', bgColor: '#FEF2F2' }
];

const platformOptions = [
  { value: 'all', label: 'All Platforms' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' }
];

const releaseTypeOptions = [
  { value: 'major', label: 'Major', color: '#DC2626' },
  { value: 'minor', label: 'Minor', color: '#D97706' },
  { value: 'patch', label: 'Patch', color: '#059669' },
  { value: 'hotfix', label: 'Hotfix', color: '#DC2626' },
  { value: 'beta', label: 'Beta', color: '#7C3AED' },
  { value: 'rc', label: 'Release Candidate', color: '#2563EB' }
];

const VersionManagement = () => {
  const { token } = useSelector(state => state.user);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [totalVersions, setTotalVersions] = useState(0);

  const [filters, setFilters] = useState({
    appType: '',
    platform: '',
    isActive: '',
    isLatest: ''
  });

  const [formData, setFormData] = useState({
    appType: 'user',
    version: '',
    platform: 'all',
    description: '',
    releaseNotes: '',
    features: [''],
    bugFixes: [''],
    improvements: [''],
    breakingChanges: [''],
    isMandatory: false,
    isLatest: false,
    isActive: true,
    downloadUrls: {
      ios: '',
      android: '',
      web: ''
    },
    appStoreId: '',
    playStoreId: '',
    minSupportedVersion: '',
    maxSupportedVersion: '',
    size: '',
    releaseType: 'patch'
  });

  const fetchVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        ...(filters.appType && { appType: filters.appType }),
        ...(filters.platform && { platform: filters.platform }),
        ...(filters.isActive !== '' && { isActive: filters.isActive }),
        ...(filters.isLatest !== '' && { isLatest: filters.isLatest }),
        ...(searchTerm && { search: searchTerm })
      };

      const response = await getVersions(token, params);
      if (response.success) {
        setVersions(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalVersions(response.pagination?.total || 0);
      }
    } catch (err) {
      setError('Failed to fetch versions');
      console.error('Error fetching versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getVersionStats(token);
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, [currentPage, filters, searchTerm]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    if (formData[field].length <= 1) return;
    setFormData(prev => {
      const newArray = prev[field].filter((_, i) => i !== index);
      return { ...prev, [field]: newArray };
    });
  };

  const resetForm = () => {
    setFormData({
      appType: 'user',
      version: '',
      platform: 'all',
      description: '',
      releaseNotes: '',
      features: [''],
      bugFixes: [''],
      improvements: [''],
      breakingChanges: [''],
      isMandatory: false,
      isLatest: false,
      isActive: true,
      downloadUrls: {
        ios: '',
        android: '',
        web: ''
      },
      appStoreId: '',
      playStoreId: '',
      minSupportedVersion: '',
      maxSupportedVersion: '',
      size: '',
      releaseType: 'patch'
    });
    setEditingVersion(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (version) => {
    setEditingVersion(version._id);
    setFormData({
      appType: version.appType || 'user',
      version: version.version || '',
      platform: version.platform || 'all',
      description: version.description || '',
      releaseNotes: version.releaseNotes || '',
      features: version.features?.length ? version.features : [''],
      bugFixes: version.bugFixes?.length ? version.bugFixes : [''],
      improvements: version.improvements?.length ? version.improvements : [''],
      breakingChanges: version.breakingChanges?.length ? version.breakingChanges : [''],
      isMandatory: version.isMandatory || false,
      isLatest: version.isLatest || false,
      isActive: version.isActive !== undefined ? version.isActive : true,
      downloadUrls: {
        ios: version.downloadUrls?.ios || '',
        android: version.downloadUrls?.android || '',
        web: version.downloadUrls?.web || ''
      },
      appStoreId: version.appStoreId || '',
      playStoreId: version.playStoreId || '',
      minSupportedVersion: version.minSupportedVersion || '',
      maxSupportedVersion: version.maxSupportedVersion || '',
      size: version.size || '',
      releaseType: version.releaseType || 'patch'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!/^\d+\.\d+\.\d+$/.test(formData.version)) {
        toast.error('Version must be in format x.y.z (e.g., 1.0.0)');
        setIsSubmitting(false);
        return;
      }

      const submitData = {
        ...formData,
        features: formData.features.filter(f => f.trim()),
        bugFixes: formData.bugFixes.filter(f => f.trim()),
        improvements: formData.improvements.filter(f => f.trim()),
        breakingChanges: formData.breakingChanges.filter(f => f.trim()),
        size: formData.size ? parseFloat(formData.size) : undefined
      };

      let response;
      if (editingVersion) {
        response = await updateVersion(editingVersion, submitData, token);
      } else {
        response = await createVersion(submitData, token);
      }

      if (response.success) {
        toast.success(editingVersion ? 'Version updated successfully!' : 'Version created successfully!');
        setShowModal(false);
        resetForm();
        fetchVersions();
        fetchStats();
      } else {
        throw new Error(response.message || 'Failed to save version');
      }
    } catch (err) {
      setError(err.message || 'Failed to save version');
      toast.error(err.message || 'Failed to save version');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this version?')) return;

    try {
      const response = await deleteVersion(id, token);
      if (response.success) {
        toast.success('Version deleted successfully!');
        fetchVersions();
        fetchStats();
      } else {
        throw new Error(response.message || 'Failed to delete version');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete version');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await toggleVersionStatus(id, token);
      if (response.success) {
        toast.success(response.message || 'Status toggled successfully');
        fetchVersions();
        fetchStats();
      } else {
        throw new Error(response.message || 'Failed to toggle status');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to toggle status');
    }
  };

  const handleView = (version) => {
    setViewingVersion(version);
  };

  const closeViewModal = () => {
    setViewingVersion(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      appType: '',
      platform: '',
      isActive: '',
      isLatest: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getAppLabel = (appType) => {
    const app = appTypes.find(a => a.id === appType);
    return app ? app.label : appType;
  };

  const getAppIcon = (appType) => {
    const app = appTypes.find(a => a.id === appType);
    return app ? app.icon : '📱';
  };

  const getAppColor = (appType) => {
    const app = appTypes.find(a => a.id === appType);
    return app ? app.color : '#6B7280';
  };

  const getPlatformLabel = (platform) => {
    const option = platformOptions.find(p => p.value === platform);
    return option ? option.label : platform;
  };

  const getReleaseTypeLabel = (type) => {
    const option = releaseTypeOptions.find(r => r.value === type);
    return option ? option.label : type;
  };

  const getReleaseTypeColor = (type) => {
    const option = releaseTypeOptions.find(r => r.value === type);
    return option ? option.color : '#6B7280';
  };

  const SkeletonRow = () => (
    <tr className="skeleton-row">
      <td><div className="skeleton-cell"></div></td>
      <td><div className="skeleton-cell"></div></td>
      <td><div className="skeleton-cell"></div></td>
      <td><div className="skeleton-cell"></div></td>
      <td><div className="skeleton-cell"></div></td>
      <td><div className="skeleton-cell"></div></td>
      <td><div className="skeleton-badge"></div></td>
      <td><div className="skeleton-actions"></div></td>
    </tr>
  );

  return (
    <div className="management-page">

      <div className="page-header">
        <div className="header-left">
          <h1>Version Management</h1>
          <div className="header-stats">
            <span>Total: {totalVersions}</span>
            {stats?.overall && (
              <>
                <span>Active: {stats.overall.totalActive}</span>
                <span>Inactive: {stats.overall.totalInactive}</span>
              </>
            )}
          </div>
        </div>
        <div className="header-actions">
          <button className="create-btn" onClick={handleOpenCreate}>
            + Create Version
          </button>
        </div>
      </div>


      <div className="filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label>App Type</label>
            <select
              name="appType"
              value={filters.appType}
              onChange={handleFilterChange}
            >
              <option value="">All Apps</option>
              {appTypes.map(app => (
                <option key={app.id} value={app.id}>{app.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Platform</label>
            <select
              name="platform"
              value={filters.platform}
              onChange={handleFilterChange}
            >
              <option value="">All Platforms</option>
              {platformOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              name="isActive"
              value={filters.isActive}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Latest</label>
            <select
              name="isLatest"
              value={filters.isLatest}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="true">Latest</option>
              <option value="false">Not Latest</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <input
              type="text"
              placeholder="Search versions..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>

          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>


      {stats && (
        <div className="stats-cards">
          <div className="stat-card">
            <h3>Total Versions</h3>
            <p>{stats.overall.totalVersions}</p>
            <span>Across all apps</span>
          </div>
          <div className="stat-card">
            <h3>Active</h3>
            <p>{stats.overall.totalActive}</p>
            <span>Active versions</span>
          </div>
          <div className="stat-card">
            <h3>Inactive</h3>
            <p>{stats.overall.totalInactive}</p>
            <span>Inactive versions</span>
          </div>
          <div className="stat-card">
            <h3>Apps</h3>
            <p>{stats.overall.totalApps}</p>
            <span>Total apps</span>
          </div>
        </div>
      )}


      {error && (
        <div className="error-message">
          {error}
        </div>
      )}


      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>App</th>
              <th>Version</th>
              <th>Platform</th>
              <th>Release Type</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : versions.length > 0 ? (
              versions.map((version, index) => (
                <tr key={version._id} className={version.isLatest ? 'latest-row' : ''}>
                  <td>{(currentPage - 1) * pageSize + index + 1}</td>
                  <td>
                    <div className="app-badge" style={{
                      backgroundColor: getAppColor(version.appType) + '20',
                      color: getAppColor(version.appType)
                    }}>
                      <span>{getAppIcon(version.appType)}</span>
                      {getAppLabel(version.appType)}
                    </div>
                  </td>
                  <td>
                    <strong>{version.version}</strong>
                    {version.isLatest && (
                      <span className="latest-badge">Latest</span>
                    )}
                  </td>
                  <td>{getPlatformLabel(version.platform)}</td>
                  <td>
                    <span className="release-type-badge" style={{
                      backgroundColor: getReleaseTypeColor(version.releaseType) + '20',
                      color: getReleaseTypeColor(version.releaseType)
                    }}>
                      {getReleaseTypeLabel(version.releaseType)}
                    </span>
                  </td>
                  <td>
                    <div className="description-cell" title={version.description}>
                      {version.description?.length > 30 
                        ? version.description.substring(0, 30) + '...' 
                        : version.description}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${version.isActive ? 'active' : 'inactive'}`}>
                      {version.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => handleView(version)}
                        title="View Details"
                      >
                        👁
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={() => handleOpenEdit(version)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className={`action-btn ${version.isActive ? 'deactivate' : 'activate'}`}
                        onClick={() => handleToggleStatus(version._id)}
                        title={version.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {version.isActive ? '🔴' : '🟢'}
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(version._id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  <p>No versions found</p>
                  <button className="create-btn" onClick={handleOpenCreate}>
                    Create your first version
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {versions.length} of {totalVersions} versions
            (Page {currentPage} of {totalPages})
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNumber}
                    className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}


      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content1 version-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingVersion ? 'Edit Version' : 'Create New Version'}</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="version-form">
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>App Type *</label>
                    <select
                      name="appType"
                      value={formData.appType}
                      onChange={handleInputChange}
                      required
                    >
                      {appTypes.map(app => (
                        <option key={app.id} value={app.id}>{app.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Version *</label>
                    <input
                      type="text"
                      name="version"
                      value={formData.version}
                      onChange={handleInputChange}
                      placeholder="e.g., 1.0.0"
                      required
                    />
                    <small>Format: major.minor.patch</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Platform</label>
                    <select
                      name="platform"
                      value={formData.platform}
                      onChange={handleInputChange}
                    >
                      {platformOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Release Type</label>
                    <select
                      name="releaseType"
                      value={formData.releaseType}
                      onChange={handleInputChange}
                    >
                      {releaseTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of this version"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Release Notes</label>
                  <textarea
                    name="releaseNotes"
                    value={formData.releaseNotes}
                    onChange={handleInputChange}
                    placeholder="Detailed release notes..."
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label>Features</label>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="array-input-group">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleArrayFieldChange('features', index, e.target.value)}
                        placeholder={`Feature ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeArrayItem('features', index)}
                        disabled={formData.features.length <= 1}
                      >
                        ×
                      </button>
                      {index === formData.features.length - 1 && (
                        <button
                          type="button"
                          className="add-btn"
                          onClick={() => addArrayItem('features')}
                        >
                          +
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="form-group">
                  <label>Bug Fixes</label>
                  {formData.bugFixes.map((bug, index) => (
                    <div key={index} className="array-input-group">
                      <input
                        type="text"
                        value={bug}
                        onChange={(e) => handleArrayFieldChange('bugFixes', index, e.target.value)}
                        placeholder={`Bug Fix ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeArrayItem('bugFixes', index)}
                        disabled={formData.bugFixes.length <= 1}
                      >
                        ×
                      </button>
                      {index === formData.bugFixes.length - 1 && (
                        <button
                          type="button"
                          className="add-btn"
                          onClick={() => addArrayItem('bugFixes')}
                        >
                          +
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="form-group">
                  <label>Improvements</label>
                  {formData.improvements.map((improvement, index) => (
                    <div key={index} className="array-input-group">
                      <input
                        type="text"
                        value={improvement}
                        onChange={(e) => handleArrayFieldChange('improvements', index, e.target.value)}
                        placeholder={`Improvement ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeArrayItem('improvements', index)}
                        disabled={formData.improvements.length <= 1}
                      >
                        ×
                      </button>
                      {index === formData.improvements.length - 1 && (
                        <button
                          type="button"
                          className="add-btn"
                          onClick={() => addArrayItem('improvements')}
                        >
                          +
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>App Store ID</label>
                    <input
                      type="text"
                      name="appStoreId"
                      value={formData.appStoreId}
                      onChange={handleInputChange}
                      placeholder="iOS App Store ID"
                    />
                  </div>
                  <div className="form-group">
                    <label>Play Store ID</label>
                    <input
                      type="text"
                      name="playStoreId"
                      value={formData.playStoreId}
                      onChange={handleInputChange}
                      placeholder="Android Play Store ID"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Size (MB)</label>
                    <input
                      type="number"
                      name="size"
                      value={formData.size}
                      onChange={handleInputChange}
                      placeholder="e.g., 45.2"
                      step="0.1"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Min Supported Version</label>
                    <input
                      type="text"
                      name="minSupportedVersion"
                      value={formData.minSupportedVersion}
                      onChange={handleInputChange}
                      placeholder="e.g., 1.0.0"
                    />
                  </div>
                </div>

                <div className="form-row checkbox-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isMandatory"
                        checked={formData.isMandatory}
                        onChange={handleInputChange}
                      />
                      <span>Mandatory Update</span>
                    </label>
                  </div>
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isLatest"
                        checked={formData.isLatest}
                        onChange={handleInputChange}
                      />
                      <span>Mark as Latest</span>
                    </label>
                  </div>
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="action-btn cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn save"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : (editingVersion ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {viewingVersion && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1 view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Version Details</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="version-details">
                <div className="detail-row">
                  <label>App:</label>
                  <span className="app-badge" style={{
                    backgroundColor: getAppColor(viewingVersion.appType) + '20',
                    color: getAppColor(viewingVersion.appType)
                  }}>
                    {getAppIcon(viewingVersion.appType)} {getAppLabel(viewingVersion.appType)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Version:</label>
                  <span>
                    <strong>{viewingVersion.version}</strong>
                    {viewingVersion.isLatest && (
                      <span className="latest-badge" style={{ marginLeft: '10px' }}>Latest</span>
                    )}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Platform:</label>
                  <span>{getPlatformLabel(viewingVersion.platform)}</span>
                </div>
                <div className="detail-row">
                  <label>Release Type:</label>
                  <span className="release-type-badge" style={{
                    backgroundColor: getReleaseTypeColor(viewingVersion.releaseType) + '20',
                    color: getReleaseTypeColor(viewingVersion.releaseType)
                  }}>
                    {getReleaseTypeLabel(viewingVersion.releaseType)}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Release Date:</label>
                  <span>{new Date(viewingVersion.releaseDate).toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <label>Description:</label>
                  <span>{viewingVersion.description}</span>
                </div>
                {viewingVersion.releaseNotes && (
                  <div className="detail-row">
                    <label>Release Notes:</label>
                    <span>{viewingVersion.releaseNotes}</span>
                  </div>
                )}
                {viewingVersion.features?.length > 0 && (
                  <div className="detail-row">
                    <label>Features:</label>
                    <ul className="feature-list">
                      {viewingVersion.features.map((feature, i) => (
                        <li key={i}>✅ {feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {viewingVersion.bugFixes?.length > 0 && (
                  <div className="detail-row">
                    <label>Bug Fixes:</label>
                    <ul className="feature-list">
                      {viewingVersion.bugFixes.map((bug, i) => (
                        <li key={i}>🐛 {bug}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {viewingVersion.improvements?.length > 0 && (
                  <div className="detail-row">
                    <label>Improvements:</label>
                    <ul className="feature-list">
                      {viewingVersion.improvements.map((improvement, i) => (
                        <li key={i}>⚡ {improvement}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${viewingVersion.isActive ? 'active' : 'inactive'}`}>
                    {viewingVersion.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Mandatory:</label>
                  <span>{viewingVersion.isMandatory ? 'Yes' : 'No'}</span>
                </div>
                {viewingVersion.size && (
                  <div className="detail-row">
                    <label>Size:</label>
                    <span>{viewingVersion.size} MB</span>
                  </div>
                )}
                {viewingVersion.downloadUrls && (
                  <div className="detail-row">
                    <label>Download URLs:</label>
                    <div className="download-urls">
                      {viewingVersion.downloadUrls.ios && (
                        <a href={viewingVersion.downloadUrls.ios} target="_blank" rel="noopener noreferrer">
                          iOS
                        </a>
                      )}
                      {viewingVersion.downloadUrls.android && (
                        <a href={viewingVersion.downloadUrls.android} target="_blank" rel="noopener noreferrer">
                          Android
                        </a>
                      )}
                      {viewingVersion.downloadUrls.web && (
                        <a href={viewingVersion.downloadUrls.web} target="_blank" rel="noopener noreferrer">
                          Web
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionManagement;